/**
 * Returns the best combination of graft sizes for a given wound area.
 * Rejects any options with wastage >= 3 and chooses the next best.
 * @param {number} woundArea - Area to cover (cm²)
 * @param {string[]} sizes - Available sizes, e.g. ["2x2", "2x3", ...]
 * @returns {Array<{size: string, quantity: number}>}
 */
export function calculateBestGrafts(woundArea, sizes) {
  // Convert sizes to area
  const sizeAreas = sizes.map(s => {
    const [w, h] = s.split("x").map(Number);
    return { size: s, area: w * h };
  }).sort((a, b) => b.area - a.area); // largest first

  // Helper to calculate wastage for a given combo
  function getWastage(combo) {
    const total = combo.reduce((sum, { size, quantity }) => {
      const [w, h] = size.split("x").map(Number);
      return sum + (w * h * quantity);
    }, 0);
    return total - woundArea;
  }

  // Try all combinations up to 3 grafts, prefer fewer grafts and less wastage
  let bestCombo = null;
  let minWastage = Infinity;

  // Generate all possible combinations (up to 3 grafts for performance)
  for (let i = 0; i < sizeAreas.length; i++) {
    for (let q1 = 1; q1 <= Math.ceil(woundArea / sizeAreas[i].area); q1++) {
      let combo1 = [{ size: sizeAreas[i].size, quantity: q1 }];
      let wastage1 = getWastage(combo1);
      if (wastage1 >= 0 && wastage1 < minWastage && wastage1 < 3) {
        bestCombo = combo1;
        minWastage = wastage1;
      }
      for (let j = i + 1; j < sizeAreas.length; j++) {
        for (let q2 = 1; q2 <= Math.ceil(woundArea / sizeAreas[j].area); q2++) {
          let combo2 = [
            { size: sizeAreas[i].size, quantity: q1 },
            { size: sizeAreas[j].size, quantity: q2 },
          ];
          let wastage2 = getWastage(combo2);
          if (wastage2 >= 0 && wastage2 < minWastage && wastage2 < 3) {
            bestCombo = combo2;
            minWastage = wastage2;
          }
          for (let k = j + 1; k < sizeAreas.length; k++) {
            for (let q3 = 1; q3 <= Math.ceil(woundArea / sizeAreas[k].area); q3++) {
              let combo3 = [
                { size: sizeAreas[i].size, quantity: q1 },
                { size: sizeAreas[j].size, quantity: q2 },
                { size: sizeAreas[k].size, quantity: q3 },
              ];
              let wastage3 = getWastage(combo3);
              if (wastage3 >= 0 && wastage3 < minWastage && wastage3 < 3) {
                bestCombo = combo3;
                minWastage = wastage3;
              }
            }
          }
        }
      }
    }
  }

  // Fallback to original greedy if no combo found
  if (!bestCombo) {
    let remaining = woundArea;
    const result = [];
    for (const { size, area } of sizeAreas) {
      if (area > remaining) continue;
      const qty = Math.floor(remaining / area);
      if (qty > 0) {
        result.push({ size, quantity: qty });
        remaining -= qty * area;
      }
    }
    const smallest = sizeAreas[sizeAreas.length - 1];
    if (remaining > 0 && smallest) {
      result.push({ size: smallest.size, quantity: 1 });
      remaining -= smallest.area;
    }
    if (result.length === 0 && sizeAreas.length) {
      result.push({ size: sizeAreas[sizeAreas.length - 1].size, quantity: 1 });
    }
    return result;
  }

  return bestCombo;
}